import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import { cn } from '@/lib/utils'
import { API_HOST } from '@/lib/config'

function resolveMarkdownImageSrc(src: string | undefined): string | undefined {
  if (!src) return src
  const trimmed = src.trim()
  if (!trimmed) return trimmed
  
  // Filter out invalid image links (e.g., question-UUID without extension)
  // These are leftover from original data where images were already embedded via ![](xxx.jpg)
  if (/^question-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(-\d+)?$/i.test(trimmed)) {
    return undefined
  }
  
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) return trimmed

  const cleaned = trimmed.replace(/^\.\/+/, "")
  if (cleaned.startsWith("/images/")) {
    const path = `/media/qb_images${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }
  // Fix stale paths with extra "images/" directory (e.g. /media/qb_images/images/{hash}.jpg → /media/qb_images/{hash}.jpg)
  const qbImagesMatch = cleaned.match(/^\/media\/qb_images\/images\/([0-9a-f]{64}\.(png|jpg|jpeg|webp|gif))$/i)
  if (qbImagesMatch) {
    const path = `/media/qb_images/${qbImagesMatch[1]}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }
  if (cleaned.startsWith("/")) {
    if (!API_HOST) return cleaned
    return `${API_HOST}${cleaned}`
  }

  if (/^[0-9a-f]{64}\.(png|jpg|jpeg|webp|gif)$/i.test(cleaned)) {
    const path = `/media/qb_images/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("media/")) {
    const path = `/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("qb_images/")) {
    const path = `/media/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  if (cleaned.startsWith("images/")) {
    const inner = cleaned.slice("images/".length)
    if (/^[0-9a-f]{64}\.(png|jpg|jpeg|webp|gif)$/i.test(inner)) {
      const path = `/media/qb_images/${inner}`
      if (!API_HOST) return path
      return `${API_HOST}${path}`
    }
    const path = `/media/qb_images/${cleaned}`
    if (!API_HOST) return path
    return `${API_HOST}${path}`
  }

  return trimmed
}

interface LatexTextProps {
  children: string | undefined | null
  className?: string
  inline?: boolean
}

export const LatexText: React.FC<LatexTextProps> = ({ children, className, inline }) => {
  if (!children) return null

  const Wrapper = inline ? 'span' : 'div'

  return (
    <Wrapper className={cn("text-sm leading-relaxed", className, inline && "inline")}>
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={{
          p: ({node, ...props}) => inline ? <span {...props} /> : <p className="mb-2 last:mb-0" {...props} />,
          img: ({node, ...props}) => (
            <img
              {...props}
              src={resolveMarkdownImageSrc(props.src)}
              className={cn("max-w-full h-auto inline-block", (props as any).className)}
            />
          ),
          ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 last:mb-0" {...props} />,
          ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 last:mb-0" {...props} />,
          li: ({node, ...props}) => <li className="pl-1" {...props} />,
          a: ({node, ...props}) => <a className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2 text-gray-600" {...props} />,
          table: ({node, ...props}) => <div className="my-4 w-full overflow-y-auto"><table className="w-full border-collapse border border-border text-sm" {...props} /></div>,
          thead: ({node, ...props}) => <thead className="bg-muted" {...props} />,
          tbody: ({node, ...props}) => <tbody {...props} />,
          tr: ({node, ...props}) => <tr className="border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted" {...props} />,
          th: ({node, ...props}) => <th className="h-10 px-4 py-2 text-left align-middle font-medium text-muted-foreground border border-border [&:has([role=checkbox])]:pr-0" {...props} />,
          td: ({node, ...props}) => <td className="p-4 align-middle border border-border [&:has([role=checkbox])]:pr-0" {...props} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </Wrapper>
  )
}
